import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Trash2, Plus } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { BACKEND_URL } from "@/lib/api";

const empty = { name: "", folder: "General", category: "general", file_url: "", file_type: "pdf", restricted_to_role: "associate", description: "" };
const FOLDERS = ["General", "Guies", "Circulars", "Formularis", "Convenis", "Descomptes"];

export default function AdminDocuments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/documents")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/documents", form);
      toast.success("Creat"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Segur?")) return;
    await api.delete(`/documents/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-documents">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline text-accent">Biblioteca</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Documents</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="doc-new-btn"><Plus className="w-4 h-4 mr-2" /> Nou document</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nou document</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="doc-form-name" /></div>
              <div>
                <Label>Fitxer</Label>
                <div className="mt-2">
                  <FileUploader onUploaded={(f) => setForm({ ...form, file_url: f.url, file_type: f.file_type, name: form.name || f.filename })} />
                  {form.file_url && (
                    <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="doc-form-uploaded">
                      Pujat: <a href={`${BACKEND_URL}${form.file_url}`} target="_blank" rel="noreferrer" className="underline">{form.file_url.split('/').pop()}</a>
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <Label className="text-xs text-muted-foreground">o URL externa</Label>
                  <Input value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} placeholder="https://..." className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Carpeta</Label>
                  <Select value={form.folder} onValueChange={(v) => setForm({ ...form, folder: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FOLDERS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Tipus</Label><Input value={form.file_type} onChange={(e) => setForm({ ...form, file_type: e.target.value })} placeholder="pdf, docx, xlsx, img" /></div>
              </div>
              <div><Label>Visibilitat</Label>
                <Select value={form.restricted_to_role} onValueChange={(v) => setForm({ ...form, restricted_to_role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visitor">Públic</SelectItem>
                    <SelectItem value="associate">Només associats</SelectItem>
                    <SelectItem value="admin">Només admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Descripció</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="doc-form-save">Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {FOLDERS.map((f) => {
        const docs = items.filter((d) => d.folder === f);
        if (docs.length === 0) return null;
        return (
          <div key={f}>
            <div className="text-sm font-heading font-semibold uppercase tracking-widest text-muted-foreground mb-3">{f}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {docs.map((d) => (
                <Card key={d.id} className="rounded-2xl border-2" data-testid={`doc-card-${d.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><FileText className="w-5 h-5" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{d.name}</div>
                        <div className="text-xs text-muted-foreground uppercase">{d.file_type} · {d.restricted_to_role}</div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => remove(d.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
      {items.length === 0 && <div className="text-muted-foreground text-sm">Sense documents.</div>}
    </div>
  );
}
